import React, { FunctionComponent } from "react"
import Input from "../shared/input"
import styles from "./updateUser.module.scss"
import { Toggle } from "../shared/selectionControls"
import { UpdateUserFormErrors, UpdateUserFormI } from "./types"
import { getInitials } from "../../util/users"
import { getUpdateUserRoleOptionsByRole } from "../../util/rolePermissions/getByRole"
import { useViewerContext } from "../../context/ViewerContext"
import Policy_OrganizationMembership from "../../rolePolicy/organizationMembership"

interface UpdateUserBodyComponentPropsInterface {
    handleFirstNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleLastNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleUserRoleChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
    handleActiveUserChange: () => void
    handleFormBlur: () => void
    form: UpdateUserFormI
    initialActivationStatus: boolean
    formErrors: UpdateUserFormErrors
    organizationName: string
    email: string
}

const Form: FunctionComponent<UpdateUserBodyComponentPropsInterface> = ({
    email,
    organizationName,
    form,
    initialActivationStatus,
    formErrors,
    handleFirstNameChange,
    handleLastNameChange,
    handleUserRoleChange,
    handleActiveUserChange,
    handleFormBlur,
}) => {
    // hooks
    const { getViewer } = useViewerContext()
    const { role: viewerRole } = getViewer()

    // constants
    const initials = getInitials(form.firstName, form.lastName)
    const isActivationStatusUpdated =
        form.activeUser !== initialActivationStatus

    return (
        <>
            <div className={styles.bodyContainerHeader}>
                <div className={styles.userIcon}>{initials}</div>
                <div className={styles.headerName}>
                    <span>{`${form.firstName} ${form.lastName}`}</span>
                    <div className={styles.headerEmailDiv}>{email}</div>
                </div>
            </div>
            <div className={styles.bodyContainer}>
                <div className={styles.rowContainer}>
                    <div className={styles.row_leftContainer}>
                        <Input
                            orientation="vertical"
                            type="text"
                            value={form.firstName}
                            onChange={handleFirstNameChange}
                            label="First Name *"
                            disabled={isActivationStatusUpdated}
                            name="firstName"
                            onBlur={handleFormBlur}
                            message={formErrors.firstName}
                            specializedClass={
                                formErrors.firstName ? "danger" : "default"
                            }
                        />
                    </div>
                    <div className={styles.row_rightContainer}>
                        <Input
                            orientation="vertical"
                            type="text"
                            value={form.lastName}
                            onChange={handleLastNameChange}
                            label="Last Name *"
                            disabled={isActivationStatusUpdated}
                            name="lastName"
                            onBlur={handleFormBlur}
                            message={formErrors.lastName}
                            specializedClass={
                                formErrors.lastName ? "danger" : "default"
                            }
                        />
                    </div>
                </div>

                <div className={styles.rowContainer}>
                    <div className={styles.organizationDiv}>
                        <div className={styles.organizationDivHeading}>
                            Organization
                        </div>
                        <div className={styles.organizationDivText}>
                            {organizationName}
                        </div>
                    </div>
                    <div className={styles.row_rightContainer}>
                        <Input
                            onChange={handleUserRoleChange}
                            options={getUpdateUserRoleOptionsByRole(viewerRole)}
                            orientation="vertical"
                            type="select"
                            value={form.userRole}
                            label="Role"
                            specializedClass="default"
                            name="userRole"
                            disabled={isActivationStatusUpdated}
                        />
                    </div>
                </div>
                {Policy_OrganizationMembership.canUpdateSubjectActivationStatus(
                    viewerRole
                ) && (
                    <>
                        <div className={styles.userActivationToggleContainer}>
                            <Toggle
                                checked={form.activeUser}
                                handleToggleClick={handleActiveUserChange}
                                toggleLabel=""
                            />
                            <div className={styles.userActivationText}>
                                <b>User : </b>
                                <span>{`${
                                    form.activeUser ? "Activate" : "Inactive"
                                } `}</span>
                            </div>
                        </div>
                        {isActivationStatusUpdated && (
                            <div className={styles.userActivationWarningText}>
                                If you change the user activation status, any
                                updates to first name, last name, or role will
                                not be submitted. Please complete those
                                operations in a seperate step.
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    )
}

export default Form
